type DeferredDeepLinkFingerprintProfile = {
  ua: string;
  platform: string;
  language: string;
  languages: string[];
  timeZoneOffset: number;
  screen: string;
  maxTouchPoints: number;
};

function normalizeUAForDeferredDeepLink(ua: string): string {
  return ua
    .replace(/;\s*wv\)/gi, ")")
    .replace(/\bVersion\/[\d.]+\s*/gi, "")
    .replace(/\bChrome\/[\d.]+/gi, "Chrome/*")
    .replace(/\bCriOS\/[\d.]+/gi, "CriOS/*")
    .replace(/\bFxiOS\/[\d.]+/gi, "FxiOS/*")
    .replace(/\bEdgiOS\/[\d.]+/gi, "EdgiOS/*")
    .replace(/\s*\bSafari\/[\d.]+/gi, "")
    .replace(/\bOS \d+_\d+(_\d+)?/g, "OS *")
    .replace(/\bMobile\/[0-9A-Z]+/gi, "Mobile/*")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function normalizeLanguageTag(language: string): string {
  // The first item of the list, without its weight and its region.
  return beforeFirst(beforeFirst(beforeFirst(language, ","), ";"), "-")
    .trim()
    .toLowerCase();
}

// What `value.split(separator)[0]` reads: the whole value without the separator.
function beforeFirst(value: string, separator: string): string {
  const index = value.indexOf(separator);
  return index === -1 ? value : value.slice(0, index);
}

function screenBucketForDeferredDeepLink(): string {
  const { width, height } = globalThis.screen;
  const shortest = Math.min(width, height);
  const longest = Math.max(width, height);
  const dpr = Math.round(globalThis.devicePixelRatio * 2) / 2;

  return `${shortest}x${longest}@${dpr}`;
}

function getDeferredDeepLinkFingerprintProfile(): DeferredDeepLinkFingerprintProfile {
  const nav = globalThis.navigator;
  const languages = [
    ...new Set(
      nav.languages
        .map((language) => normalizeLanguageTag(language))
        .filter((language) => language !== "")
    ),
  ].sort();

  return {
    ua: normalizeUAForDeferredDeepLink(nav.userAgent),
    platform: nav.platform.trim().toLowerCase(),
    language: normalizeLanguageTag(nav.language),
    languages,
    timeZoneOffset: new Date().getTimezoneOffset(),
    screen: screenBucketForDeferredDeepLink(),
    maxTouchPoints: Math.max(0, nav.maxTouchPoints),
  };
}

function basicHash(input: string): string {
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    const codePoint = input.codePointAt(i);
    if (codePoint === undefined) {
      throw new Error(`Invariant: string index ${i} is out of bounds`);
    }
    hash = (hash << 5) + hash + codePoint;
    hash &= hash;
  }

  const hex = Math.abs(hash).toString(16);

  let hash2 = 0;
  for (let i = 0; i < input.length; i++) {
    const codePoint = input.codePointAt(i);
    if (codePoint === undefined) {
      throw new Error(`Invariant: string index ${i} is out of bounds`);
    }
    hash2 = (hash2 << 3) + hash2 + codePoint * 7;
    hash2 &= hash2;
  }

  const hex2 = Math.abs(hash2).toString(16);
  return (hex + hex2).padEnd(64, "0").slice(0, 64);
}

async function hashProfile(profile: unknown): Promise<string> {
  const json = JSON.stringify(profile);

  // `crypto.subtle` exists in a secure context only (https, localhost).
  if (globalThis.isSecureContext) {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(json);
      const digest = await crypto.subtle.digest("SHA-256", data);

      const bytes = new Uint8Array(digest);
      return [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
    } catch {
      return basicHash(json);
    }
  }

  return basicHash(json);
}

export async function getDeferredDeepLinkFingerprint(): Promise<{
  fingerprint: string;
  profile: DeferredDeepLinkFingerprintProfile;
}> {
  const profile = getDeferredDeepLinkFingerprintProfile();
  const fingerprint = await hashProfile(profile);

  return { fingerprint, profile };
}
